import {
  EXPORT_IN_FLIGHT_STATUSES,
  EXPORT_PREFER_ASYNC,
  type ExportRequestOutcome,
  type ExportRequestResponse,
  type ExportStatus,
  type ExportSummary,
} from '@gozoki/api-types'
import { attachmentFileName } from '@gozoki/tools'
import type { MutationStatus } from '@tanstack/react-query'
import { frenchParisDayMonthTimeOfInstant } from './iso-date.ts'

/**
 * Logique pure des exports en tâche de fond vus du back-office (lot S8.4, conception S8 §2.9, §3.3
 * et §3.4) : en-tête `Prefer`, lecture de la réponse d'une route d'export (fichier ou demande),
 * rythme du sondage et textes des toasts. Sans React ni client HTTP, testée par `node --test`.
 */

/** Calcul en file ou en cours : le suivi continue. */
export const isExportInFlight = (status: ExportStatus): boolean =>
  (EXPORT_IN_FLIGHT_STATUSES as readonly ExportStatus[]).includes(status)

/** Type servi en tâche de fond pour cet admin (`GET /exports/active-types`) ; liste inconnue : non. */
export const isAsyncExportType = (
  type: string,
  activeTypes: readonly string[] | undefined,
): boolean => activeTypes?.includes(type) ?? false

/**
 * En-têtes de l'appel d'une route d'export : `Prefer: respond-async` seulement pour un type que le
 * backend sert en tâche de fond pour cet admin. Liste inconnue (chargement, backend d'avant S8.3) :
 * rien, la route répond par le fichier comme avant.
 */
export const exportRequestHeaders = (
  type: string,
  activeTypes: readonly string[] | undefined,
): Record<string, string> =>
  isAsyncExportType(type, activeTypes) ? { Prefer: EXPORT_PREFER_ASYNC } : {}

/**
 * Lecture d'une réponse de route d'export par son `Content-Type`, avant le corps : JSON, la demande
 * en tâche de fond (`202`, `ExportRequestResponse`) ; autre chose, le fichier de la voie synchrone
 * (type inactif, backend pas encore déployé, onglet resté ouvert d'avant).
 */
export const exportResponseKind = (contentType: string | null | undefined): 'job' | 'file' =>
  contentType?.split(';')[0]?.trim().toLowerCase() === 'application/json' ? 'job' : 'file'

const EXPORT_REQUEST_OUTCOMES = [
  'created',
  'joined',
  'reused',
] as const satisfies readonly ExportRequestOutcome[]

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/**
 * Corps `202` d'une route d'export : le seul JSON que le hook accepte. Issue connue, identifiant,
 * statut et date de la demande exigés : ce que lisent le suivi et les toasts.
 */
export const isExportRequestResponse = (body: unknown): body is ExportRequestResponse => {
  if (!isRecord(body) || !(EXPORT_REQUEST_OUTCOMES as readonly unknown[]).includes(body.outcome)) {
    return false
  }
  const summary = body.export
  return (
    isRecord(summary) &&
    typeof summary.id === 'string' &&
    typeof summary.status === 'string' &&
    typeof summary.createdAt === 'string'
  )
}

export const UNEXPECTED_EXPORT_RESPONSE = 'Réponse inattendue du serveur.'

/** Réponse d'une route d'export : le fichier de la voie synchrone, ou la demande en tâche de fond. */
export type ExportRouteResponse =
  | { kind: 'file'; blob: Blob; fileName: string }
  | { kind: 'job'; body: ExportRequestResponse }

const parseJson = async (blob: Blob): Promise<unknown> => {
  try {
    return JSON.parse(await blob.text())
  } catch {
    return null
  }
}

/**
 * Lecture d'une route d'export appelée en `Blob`, par son statut et ses en-têtes : `202` en JSON,
 * la demande (`ExportRequestResponse` complet) ; autre `Content-Type`, le fichier, nommé par son
 * `Content-Disposition` (exposé par le CORS du backend) ou `fallbackFileName`. Un JSON d'un autre
 * statut ou d'une autre forme est refusé plutôt que suivi.
 */
export const readExportRouteResponse = async (
  blob: Blob,
  response: { status: number; headers: Headers },
  fallbackFileName: string,
): Promise<ExportRouteResponse> => {
  if (exportResponseKind(response.headers.get('content-type') ?? blob.type) === 'file') {
    const disposition = response.headers.get('content-disposition')
    return { kind: 'file', blob, fileName: attachmentFileName(disposition, fallbackFileName) }
  }
  const body = response.status === 202 ? await parseJson(blob) : null
  if (!isExportRequestResponse(body)) throw new Error(UNEXPECTED_EXPORT_RESPONSE)
  return { kind: 'job', body }
}

/** Sondage de `GET /exports/:id` : toutes les 2 s pendant 30 s, puis toutes les 5 s. */
export const EXPORT_POLL_FAST_MS = 2000
export const EXPORT_POLL_SLOW_MS = 5000
const EXPORT_POLL_FAST_WINDOW_MS = 30_000

/**
 * Délai avant le prochain sondage, depuis le début du suivi ; `false` au statut final, qui
 * arrête le sondage. Il suffit seul : l'évènement `export:updated` ne fait que l'avancer.
 */
export const exportPollDelay = (status: ExportStatus, elapsedMs: number): number | false => {
  if (!isExportInFlight(status)) return false
  return elapsedMs < EXPORT_POLL_FAST_WINDOW_MS ? EXPORT_POLL_FAST_MS : EXPORT_POLL_SLOW_MS
}

/**
 * Refus définitif d'un sondage (export introuvable, droit retiré, demande invalide) : le suivi
 * s'arrête en échec. Erreur serveur, réseau coupé, délai ou `429` : le sondage suivant réessaie.
 */
export const isDefinitivePollRefusal = (status: number | null): boolean =>
  status !== null && status >= 400 && status < 500 && status !== 408 && status !== 429

/** État d'un bouton d'export (`useExportJob`). */
export type ExportJobState = 'idle' | 'starting' | 'queued' | 'running' | 'ready' | 'failed'

/**
 * État du bouton : la demande (statut de la mutation), puis l'export qu'elle suit s'il y en a un.
 * Sans export suivi, une demande réussie a rendu son fichier (voie synchrone, fichier déjà prêt).
 */
export const exportJobState = (
  request: MutationStatus,
  trackedStatus: ExportStatus | undefined,
): ExportJobState => {
  if (request === 'pending') return 'starting'
  if (request === 'error') return 'failed'
  if (trackedStatus === undefined) return request === 'success' ? 'ready' : 'idle'
  if (trackedStatus === 'pending') return 'queued'
  if (trackedStatus === 'running' || trackedStatus === 'ready') return trackedStatus
  return trackedStatus === 'cancelled' ? 'idle' : 'failed'
}

interface ExportToastText {
  title: string
  description: string
}

/** Toast de la réponse `202`, selon son issue. */
export const exportRequestedToast = (
  {
    export: summary,
    outcome,
  }: Pick<ExportRequestResponse, 'outcome'> & {
    export: Pick<ExportSummary, 'createdAt'>
  },
  label: string,
): ExportToastText => {
  if (outcome === 'reused') {
    const dataAsOf = frenchParisDayMonthTimeOfInstant(summary.createdAt)
    return {
      title: `Export identique déjà disponible (données au ${dataAsOf})`,
      description: `${label} · Téléchargement en cours.`,
    }
  }
  return {
    title: 'Export lancé',
    description:
      outcome === 'joined'
        ? `${label} · Un export identique est déjà en cours : le fichier se téléchargera à sa fin.`
        : `${label} · Le fichier se téléchargera à la fin du calcul : vous pouvez quitter la page.`,
  }
}

/** Titre du toast d'un export en échec. */
export const EXPORT_FAILED_TITLE = 'Échec de l’export'

/** Repli d'un échec sans message du backend. */
export const EXPORT_FAILED_FALLBACK = 'L’export a échoué.'

export type ExportToastKind = 'success' | 'error' | 'info'

/** Toast d'un export arrivé à son statut final. */
export const exportSettledToast = (
  summary: Pick<ExportSummary, 'status' | 'error' | 'fileName'>,
  label: string,
): ExportToastText & { kind: ExportToastKind } => {
  switch (summary.status) {
    case 'ready': {
      return {
        kind: 'success',
        title: 'Export prêt',
        description: `${label} · ${summary.fileName}`,
      }
    }
    case 'cancelled': {
      return { kind: 'info', title: 'Export annulé', description: label }
    }
    case 'expired': {
      return {
        kind: 'error',
        title: EXPORT_FAILED_TITLE,
        description: `${label} · Ce fichier a expiré : relancez l’export.`,
      }
    }
    default: {
      return {
        kind: 'error',
        title: EXPORT_FAILED_TITLE,
        description: `${label} · ${summary.error ?? EXPORT_FAILED_FALLBACK}`,
      }
    }
  }
}
